import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useIndirectTaxAvailableRegistrations,
  useIndirectTaxRegistrationConditions,
  useIndirectTaxRegistrationPortalFields,
  useIndirectTaxRegistrationTaxTypeDetails,
  useIndirectTaxRegistrations,
  updateIndirectTaxRegistration,
  requestIndirectTaxRegistrationsValidationBulk,
  IIndirectTaxTypeDetails,
} from '@commenda/api/indirect-tax/registrations';
import {
  createIndirectTaxCredentialsBulk,
  updateIndirectTaxCredential,
  useIndirectTaxCredential,
  useIndirectTaxCredentials,
  IndirectTaxCredentialBlockType,
  IndirectTaxCredentialType,
  IIndirectTaxCredentialBlockInput,
} from '@commenda/api/new-sales-tax/credentials';
import type {
  IIndirectTaxRegistrationV2WithSettings,
  IIndirectTaxUpdateRegistrationV2Params,
  IIndirectTaxPortal,
} from '@commenda/api/indirect-tax/registrations/types';
import { trimIndirectTaxCredentialFieldValue } from '../../utils/trimIndirectTaxCredentialFieldValue';

export enum EditWizardStep {
  REGISTRATION_INFO = 1,
  CONDITIONS = 2,
  CREDENTIALS = 3,
}

interface EditFormState {
  taxTypes: string[];
  frequency: string;
  effectiveStartDate: string;
  portalId: string;
  registrationFields: Record<string, string>;
  credentialFields: Record<string, string>;
}

export function useIndirectTaxEditRegistrationWizard({
  isOpen,
  corporationId,
  registrationData,
}: {
  isOpen: boolean;
  corporationId: number;
  registrationData: IIndirectTaxRegistrationV2WithSettings;
}) {
  const [currentStep, setCurrentStep] = useState<EditWizardStep>(EditWizardStep.REGISTRATION_INFO);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldsPrefilled, setFieldsPrefilled] = useState(false);

  const initialForm: EditFormState = useMemo(() => ({
    taxTypes: registrationData.taxTypes ?? [],
    frequency: registrationData.frequencies?.[0] ?? '',
    effectiveStartDate: registrationData.effectiveStartDate ?? '',
    portalId: '',
    registrationFields: {},
    credentialFields: {},
  }), [registrationData]);

  const [form, setForm] = useState<EditFormState>(initialForm);

  // --- Data fetching ---

  // Fetch available registrations for this jurisdiction to get tax type options + portal IDs
  const { data: availableRegistrations, isLoading: isLoadingRegistrations } =
    useIndirectTaxAvailableRegistrations(
      isOpen && registrationData.jurisdictionId
        ? { corporationId, jurisdictionIds: [registrationData.jurisdictionId] }
        : null
    );

  // Find the registration and tax type option matching the current registration
  const selectedRegistration = useMemo(
    () => {
      if (!availableRegistrations?.length) return null;
      for (const reg of availableRegistrations) {
        const match = reg.taxTypeOptions.find(
          (opt) => opt.registrationTaxTypeId === registrationData.registrationTaxTypeId
        );
        if (match) return reg;
      }
      return availableRegistrations[0] ?? null;
    },
    [availableRegistrations, registrationData.registrationTaxTypeId]
  );

  const selectedTaxTypeOption = useMemo(
    () =>
      selectedRegistration?.taxTypeOptions.find(
        (o) => o.registrationTaxTypeId === registrationData.registrationTaxTypeId
      ) ?? null,
    [selectedRegistration, registrationData.registrationTaxTypeId]
  );

  // Tax type details for display names
  const { data: taxTypeDetailsData } = useIndirectTaxRegistrationTaxTypeDetails(
    isOpen && registrationData.jurisdictionId
      ? { corporationId, jurisdictionIds: [registrationData.jurisdictionId] }
      : null
  );

  const taxTypeDetailsMap = useMemo(() => {
    const map: Record<string, IIndirectTaxTypeDetails> = {};
    taxTypeDetailsData?.taxTypeDetails?.forEach((detail) => {
      map[detail.taxType] = {
        officialName: detail.officialName,
        displayName: detail.displayName,
        shortDescription: detail.shortDescription,
        fullDescription: detail.fullDescription,
        recommendedFor: detail.recommendedFor,
      };
    });
    return map;
  }, [taxTypeDetailsData]);

  // Fetch conditions to determine if conditions step is needed and to show read-only values
  const { data: conditions, isLoading: isLoadingConditions } =
    useIndirectTaxRegistrationConditions(
      isOpen && registrationData.registrationTaxTypeId
        ? { corporationId, registrationTaxTypeId: registrationData.registrationTaxTypeId }
        : null
    );

  const prerequisiteCondition = useMemo(
    () => conditions.find((c) => c.type === 'PREREQUISITE_REGISTRATION') ?? null,
    [conditions]
  );

  const memberCountryCondition = useMemo(
    () => conditions.find((c) => c.type === 'MEMBER_COUNTRY_TO_REGISTER_THROUGH') ?? null,
    [conditions]
  );

  const hasInteractiveConditions = conditions.some(
    (c) => c.type === 'PREREQUISITE_REGISTRATION' || c.type === 'MEMBER_COUNTRY_TO_REGISTER_THROUGH'
  );

  // Fetch existing registrations to resolve prerequisite's registrationTaxTypeId from its UUID
  const { data: existingRegistrations } = useIndirectTaxRegistrations({
    filters: { corporationId },
  });

  // For Union OSS: the registration stores memberStateRegistrationId (UUID).
  // Resolve it to a registrationTaxTypeId for the conditions step display.
  const resolvedPrerequisiteTaxTypeId = useMemo(() => {
    if (!registrationData.memberStateRegistrationId || !existingRegistrations) return '';
    const prereqReg = existingRegistrations.find(
      (r) => r.id === registrationData.memberStateRegistrationId
    );
    return prereqReg?.registrationTaxTypeId ?? '';
  }, [registrationData.memberStateRegistrationId, existingRegistrations]);

  // --- Portal resolution ---

  // Direct portal IDs from tax type option
  const directPortalIds = selectedTaxTypeOption?.portalIds ?? [];

  // Fallback for trade-bloc: resolve portal from conditions via registeredThroughRegistrationTaxTypeId
  const portalIds = useMemo(() => {
    if (directPortalIds.length > 0) return directPortalIds;
    if (!conditions?.length || !registrationData.registeredThroughRegistrationTaxTypeId) return [];

    for (const condition of conditions) {
      const match = condition.registrationOptions.find(
        (opt) => opt.registrationTaxTypeId === registrationData.registeredThroughRegistrationTaxTypeId
      );
      if (match?.portalIds?.length) return match.portalIds;
    }
    return [];
  }, [directPortalIds, conditions, registrationData.registeredThroughRegistrationTaxTypeId]);

  // Fetch portal fields
  const { data: portalFieldsData, isLoading: isLoadingPortals } =
    useIndirectTaxRegistrationPortalFields(
      isOpen && portalIds.length > 0
        ? { corporationId, portalIds }
        : null
    );

  const portals: IIndirectTaxPortal[] = portalFieldsData?.portals ?? [];

  const selectedPortal = useMemo(
    () => portals.find((p) => p.portalId === form.portalId) ?? null,
    [portals, form.portalId]
  );

  // Auto-select portal if only one
  useEffect(() => {
    if (portals.length === 1 && !form.portalId) {
      setForm((prev) => ({ ...prev, portalId: portals[0].portalId }));
    }
  }, [portals, form.portalId]);

  // --- Credential prefill ---

  const existingCredentialId = registrationData.credentialId ?? '';

  // Check for portal-level credentials (shared across registrations)
  const { data: credentialsByPortal, isLoading: isCheckingCredentials } =
    useIndirectTaxCredentials(
      isOpen && form.portalId
        ? { portalIds: [form.portalId], corporationId }
        : null
    );

  const credentialsFound = (credentialsByPortal?.length ?? 0) > 0;

  // The credential this registration saves to: its own, or else the corporation's existing
  // credential for the portal. A corporation holds one credential per portal, so creating a
  // second one is rejected.
  const credentialIdToReuse = existingCredentialId || credentialsByPortal?.[0]?.id || '';
  const isResolvingCredential = !existingCredentialId && isCheckingCredentials;

  const { data: existingCredentialData, isLoading: isLoadingExistingCredential } =
    useIndirectTaxCredential(
      isOpen && credentialIdToReuse ? credentialIdToReuse : null,
      isOpen && credentialIdToReuse ? { corporationId } : null
    );

  // Prefill credential fields from existing credential data
  useEffect(() => {
    if (!isOpen || fieldsPrefilled || !selectedPortal) return;
    if (isResolvingCredential || isLoadingExistingCredential) return;

    const portalFields = selectedPortal.credentialFields ?? [];
    const credentialLevelKeys = new Set(
      portalFields.filter((f) => !f.isUniquePerRegistration).map((f) => f.credentialKey)
    );

    const prefillRegistrationFields: Record<string, string> = {};
    const prefillCredentialFields: Record<string, string> = {};

    // A credential belongs to the corporation and portal, and other registrations may share it,
    // so only its credential-level values are prefilled. Registration-level blocks it still holds
    // from older registrations are ignored.
    existingCredentialData?.blocks?.forEach((block) => {
      if (block.value && credentialLevelKeys.has(block.blockKey)) {
        prefillCredentialFields[block.blockKey] = block.value;
      }
    });

    // Registration-level values come from the registration itself.
    const taxRegNumber = registrationData.taxRegistrationNumber;
    const firstRegistrationField = portalFields.find((f) => f.isUniquePerRegistration);
    if (taxRegNumber && firstRegistrationField) {
      prefillRegistrationFields[firstRegistrationField.credentialKey] = taxRegNumber;
    }

    setForm((prev) => ({
      ...prev,
      registrationFields: { ...prefillRegistrationFields, ...prev.registrationFields },
      credentialFields: { ...prefillCredentialFields, ...prev.credentialFields },
    }));
    setFieldsPrefilled(true);
  }, [
    isOpen,
    fieldsPrefilled,
    selectedPortal,
    existingCredentialData,
    isResolvingCredential,
    isLoadingExistingCredential,
    registrationData.taxRegistrationNumber,
  ]);

  // --- Actions ---

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setCurrentStep(EditWizardStep.REGISTRATION_INFO);
    setIsSubmitting(false);
    setFieldsPrefilled(false);
  }, [initialForm]);

  const setOptionalTaxTypes = useCallback((taxTypes: string[]) => {
    setForm((prev) => ({ ...prev, taxTypes }));
  }, []);

  const setFrequency = useCallback((frequency: string) => {
    setForm((prev) => ({ ...prev, frequency }));
  }, []);

  const setEffectiveStartDate = useCallback((effectiveStartDate: string) => {
    setForm((prev) => ({ ...prev, effectiveStartDate }));
  }, []);

  const setRegistrationField = useCallback((key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      registrationFields: { ...prev.registrationFields, [key]: value },
    }));
  }, []);

  const setCredentialField = useCallback((key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      credentialFields: { ...prev.credentialFields, [key]: value },
    }));
  }, []);

  // --- Navigation ---

  // Union OSS prerequisite registrations don't need a separate credentials step
  const shouldSkipCredentials = prerequisiteCondition !== null && memberCountryCondition === null;

  const canProceedStep1 = useMemo(() => {
    return !!(form.taxTypes.length > 0 && form.frequency && form.effectiveStartDate);
  }, [form.taxTypes, form.frequency, form.effectiveStartDate]);

  // Conditions step is always valid in edit mode (read-only)
  const canProceedStep2 = true;

  const canProceedStep3 = useMemo(() => {
    if (!selectedPortal) return false;
    // Wait for the portal lookup, or submit would create a duplicate credential.
    if (isResolvingCredential || isLoadingExistingCredential) return false;
    const allFields = selectedPortal.credentialFields ?? [];
    const requiredRegFields = allFields.filter((f) => f.isUniquePerRegistration && f.required);
    const hasRegFields = requiredRegFields.every(
      (f) =>
        !!trimIndirectTaxCredentialFieldValue(
          form.registrationFields[f.credentialKey] || '',
          f.credentialType
        )
    );
    if (!hasRegFields) return false;
    const requiredCredFields = allFields.filter((f) => !f.isUniquePerRegistration && f.required);
    return requiredCredFields.every(
      (f) =>
        !!trimIndirectTaxCredentialFieldValue(
          form.credentialFields[f.credentialKey] || '',
          f.credentialType
        )
    );
  }, [selectedPortal, form, isResolvingCredential, isLoadingExistingCredential]);

  const goToNextStep = useCallback(() => {
    if (currentStep === EditWizardStep.REGISTRATION_INFO) {
      if (hasInteractiveConditions) {
        setCurrentStep(EditWizardStep.CONDITIONS);
      } else if (shouldSkipCredentials) {
        return; // No conditions, no credentials — shouldn't happen but handle
      } else {
        setCurrentStep(EditWizardStep.CREDENTIALS);
      }
    } else if (currentStep === EditWizardStep.CONDITIONS) {
      if (shouldSkipCredentials) {
        return; // Submit directly from conditions
      }
      setCurrentStep(EditWizardStep.CREDENTIALS);
    }
  }, [currentStep, hasInteractiveConditions, shouldSkipCredentials]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep === EditWizardStep.CREDENTIALS) {
      if (hasInteractiveConditions) {
        setCurrentStep(EditWizardStep.CONDITIONS);
      } else {
        setCurrentStep(EditWizardStep.REGISTRATION_INFO);
      }
    } else if (currentStep === EditWizardStep.CONDITIONS) {
      setCurrentStep(EditWizardStep.REGISTRATION_INFO);
    }
  }, [currentStep, hasInteractiveConditions]);

  // --- Submit ---

  const submit = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    setIsSubmitting(true);

    try {
      // Resolve credentials
      let credentialIdToUse = existingCredentialId;

      if (!shouldSkipCredentials && form.portalId && selectedPortal) {
        const credentialLevelFields =
          selectedPortal.credentialFields?.filter((f) => !f.isUniquePerRegistration) ?? [];

        if (credentialLevelFields.length > 0) {
          const blocks: IIndirectTaxCredentialBlockInput[] = credentialLevelFields.map((field) => {
            const existingBlock = existingCredentialData?.blocks?.find(
              (b) => b.blockKey === field.credentialKey
            );

            return {
              ...(existingBlock?.id ? { id: existingBlock.id } : {}),
              blockKey: field.credentialKey,
              blockLabel: field.credentialName,
              blockType:
                field.credentialType === 'SecureString'
                  ? IndirectTaxCredentialBlockType.SecureString
                  : field.credentialType === 'File'
                    ? IndirectTaxCredentialBlockType.File
                    : IndirectTaxCredentialBlockType.String,
              value: trimIndirectTaxCredentialFieldValue(
                form.credentialFields[field.credentialKey] || '',
                field.credentialType
              ),
            };
          });

          const nonEmptyBlocks = blocks.filter((b) => b.value !== '');

          if (credentialIdToReuse) {
            // Update the registration's credential, or the corporation's existing one for the portal
            const updateResp = await updateIndirectTaxCredential(credentialIdToReuse, {
              corporationId,
              portalId: selectedPortal.portalId,
              blocks,
            });

            if (!updateResp.success) {
              return {
                success: false,
                error: updateResp.error || 'Failed to update credentials.',
              };
            }
            credentialIdToUse = credentialIdToReuse;
          } else if (nonEmptyBlocks.length > 0) {
            // Create new credential
            const createResp = await createIndirectTaxCredentialsBulk({
              corporationId,
              credentials: [
                {
                  credentialType: IndirectTaxCredentialType.INDIRECT_TAX_REGISTRATION,
                  portalId: selectedPortal.portalId,
                  name: selectedPortal.portalName || 'Portal credential',
                  portalUrl: selectedPortal.portalUrl?.trim() || undefined,
                  blocks: nonEmptyBlocks,
                },
              ],
            });

            // The bulk endpoint reports a rejected item inside a successful response.
            const created = createResp.data?.credentials?.[0];
            if (!createResp.success || !created?.success || !created.id) {
              return { success: false, error: 'Failed to create credentials.' };
            }
            credentialIdToUse = created.id;
          }
        }
      }

      // Resolve tax registration number from registration-level fields.
      // Use the first non-empty registration-level field value (covers all portal naming conventions).
      const regLevelFields =
        selectedPortal?.credentialFields?.filter((f) => f.isUniquePerRegistration) ?? [];
      let taxRegistrationNumber: string | undefined;
      for (const field of regLevelFields) {
        const val = trimIndirectTaxCredentialFieldValue(
          form.registrationFields[field.credentialKey] || '',
          field.credentialType
        );
        if (val) {
          taxRegistrationNumber = val;
          break;
        }
      }

      // Resolve full frequency array (primary + mandatory related)
      const selectedFreqOption = selectedTaxTypeOption?.frequencies.find(
        (f) => f.primary === form.frequency
      );
      const requiredRelatedFreqs =
        selectedFreqOption?.related?.filter((r) => !r.optional).map((r) => r.value) ?? [];
      const allFrequencies = [form.frequency, ...requiredRelatedFreqs];

      // Update registration
      const payload: IIndirectTaxUpdateRegistrationV2Params = {
        taxTypes: form.taxTypes,
        frequencies: allFrequencies,
        effectiveStartDate: form.effectiveStartDate,
        taxRegistrationNumber: taxRegistrationNumber || undefined,
        credentialId: credentialIdToUse || undefined,
      };

      const response = await updateIndirectTaxRegistration({
        registrationId: registrationData.id,
        corporationId,
        body: payload,
      });

      if (!response.success) {
        return {
          success: false,
          error: response.error || 'Failed to update registration.',
        };
      }

      // Request validation
      await requestIndirectTaxRegistrationsValidationBulk({
        corporationId,
        registrationIds: [registrationData.id],
      });

      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update registration.';
      return { success: false, error: message };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    corporationId,
    registrationData.id,
    existingCredentialId,
    credentialIdToReuse,
    existingCredentialData,
    selectedPortal,
    selectedTaxTypeOption,
    shouldSkipCredentials,
  ]);

  return {
    // State
    currentStep,
    form,
    isSubmitting,

    // Registration data (read-only display)
    registrationData,
    selectedRegistration,
    selectedTaxTypeOption,
    taxTypeDetailsMap,

    // Available registrations for the selector component
    availableRegistrations: availableRegistrations ?? [],
    isLoadingRegistrations,

    // Conditions
    conditions,
    isLoadingConditions,
    hasInteractiveConditions,
    prerequisiteCondition,
    memberCountryCondition,
    shouldSkipCredentials,
    resolvedPrerequisiteTaxTypeId,

    // Portals + credentials
    portals,
    isLoadingPortals,
    selectedPortal,
    credentialsFound,
    isCheckingCredentials: isCheckingCredentials || isLoadingExistingCredential,

    // Navigation
    canProceedStep1,
    canProceedStep2,
    canProceedStep3,
    goToNextStep,
    goToPreviousStep,

    // Actions
    setOptionalTaxTypes,
    setFrequency,
    setEffectiveStartDate,
    setRegistrationField,
    setCredentialField,
    submit,
    resetForm,
  };
}
